/**
 * 페이앱·카드단말기·현금영수증·다수배분 신청 — 기사접수 앱(dytaxdriver) 4개 탭을 대신한다(plan/driver-orders.md ④).
 * 신청자는 로그인한 기사 본인 — 「다른 기사 대신 신청」을 고르면 그 기사(#332 · 넣은 사람은 나로 남는다). 세 종류(페이앱·카드·현금)는 거래처를 목록에 없어도 직접 쓸 수 있다(기사접수 앱과 같다).
 * 다수배분 = 배분 항목만(기사 또는 외부 업체·개인) — 1개 이상.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { driverApi, driverError, uploadDriverInvoiceFile, type DriverCustomer, type DriverInvoiceForm, type DriverOrderKind } from '../lib/driverApi';
import Screen, { ErrorBanner, Loading, SubmitError } from './Screen';
import ProxyPicker, { proxyName, type ProxyTarget } from './ProxyPicker';
import { won } from './format';

const digits = (s: string) => s.replace(/[^0-9]/g, '');
const todayKst = () => new Date(Date.now() + 9 * 3600_000).toISOString().slice(0, 10);
function amounts(raw: string, included: boolean): { supply: number; vat: number } | null {
  const n = Number(digits(raw));
  if (!n) return null;
  if (included) { const supply = Math.round(n / 1.1); return { supply, vat: n - supply }; }
  return { supply: n, vat: Math.floor(n / 10) };
}
const KINDS: { kind: DriverOrderKind; icon: string; hint: string }[] = [
  { kind: '페이앱', icon: '📱', hint: '인터넷 카드결제(결제 링크)' },
  { kind: '카드단말기', icon: '💳', hint: '카드 단말기로 결제함' },
  { kind: '현금영수증', icon: '🧾', hint: '현금 받고 영수증 발급' },
  { kind: '다수배분', icon: '🔀', hint: '여러 기사·업체에 나눠 주기' },
];
const ATTACH_LABEL: Partial<Record<DriverOrderKind, string>> = { 페이앱: '카드 사진(선택)', 카드단말기: '영수증 사진(선택)', 다수배분: '사업자등록증 등(선택)' };
type Dist = { key: number; driverId?: number; type?: '외부배분-개인' | '외부배분-법인'; label: string; amount: string };

export default function NewOrder() {
  const navigate = useNavigate();
  const [form, setForm] = useState<DriverInvoiceForm | null>(null);
  const [error, setError] = useState<string | null>(null);
  // 다수배분 탭에서 들어오면 그 종류를 골라 둔다(#226)
  const [params] = useSearchParams();
  const [kind, setKind] = useState<DriverOrderKind | null>(params.get('kind') === '다수배분' ? '다수배분' : null);
  const [corpId, setCorpId] = useState<number | null>(null);
  const [q, setQ] = useState('');
  const [found, setFound] = useState<DriverCustomer[]>([]);
  const [picked, setPicked] = useState(false);
  const [cust, setCust] = useState({ name: '', biz: '', email: '', phone: '', cashId: '' });
  const [requestDate, setRequestDate] = useState(todayKst());
  const [amount, setAmount] = useState('');
  const [included, setIncluded] = useState(false);
  const [memo, setMemo] = useState('');
  const [attach, setAttach] = useState<{ path: string; type: string; name: string } | null>(null);
  const [dists, setDists] = useState<Dist[]>([]);
  const [peerQ, setPeerQ] = useState('');
  const [peers, setPeers] = useState<{ driverId: number; label: string }[]>([]);
  const [extName, setExtName] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [proxy, setProxy] = useState<ProxyTarget | null>(null);
  const dasu = kind === '다수배분';

  // 신청 칸(법인·신청자) — 대신 신청할 기사를 고르면 그 기사 기준으로 다시 불러온다(#332).
  useEffect(() => {
    let live = true;
    setForm(null); setCorpId(null);
    driverApi.invoiceForm(proxy ? { forDriverId: proxy.driverId } : {})
      .then((f) => { if (!live) return; setForm(f); if (f.corps.length === 1) setCorpId(f.corps[0]!.corp_id); })
      .catch((e) => {
        if (!live) return;
        setError(driverError(e, '신청 화면을 열지 못했습니다.'));
        if (proxy) setProxy(null); // 대신 신청이 막히면 내 이름 화면으로 돌아간다
      });
    return () => { live = false; };
  }, [proxy]);

  /** 대신 신청할 기사 바꾸기 — 첨부는 그 기사 칸에 올라가므로 비우고, 그 기사에게 가는 배분은 뺀다. */
  function changeProxy(t: ProxyTarget | null) {
    setError(null); setAttach(null);
    if (t) setDists((xs) => xs.filter((d) => d.driverId !== t.driverId));
    setProxy(t);
  }
  useEffect(() => {
    if (picked || q.trim().length < 2) { setFound([]); return; }
    const t = setTimeout(() => { driverApi.searchCustomers({ q: q.trim() }).then((r) => setFound(r.rows)).catch(() => setFound([])); }, 300);
    return () => clearTimeout(t);
  }, [q, picked]);
  useEffect(() => {
    if (peerQ.trim().length < 2) { setPeers([]); return; }
    const t = setTimeout(() => { driverApi.searchPeers({ q: peerQ.trim() }).then((r) => setPeers(r.rows)).catch(() => setPeers([])); }, 300);
    return () => clearTimeout(t);
  }, [peerQ]);

  function pick(c: DriverCustomer) {
    setCust((x) => ({ ...x, name: c.cust_name, biz: c.biz_number ?? '', email: c.email ?? '' }));
    setQ(c.cust_name); setPicked(true); setFound([]);
  }
  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setBusy('파일 올리는 중…'); setError(null);
    try { setAttach(await uploadDriverInvoiceFile(f, proxy?.driverId)); } catch (err) { setError(driverError(err, '파일을 올리지 못했습니다.')); } finally { setBusy(null); }
  }
  const addDist = (d: Omit<Dist, 'key' | 'amount'>) => setDists((xs) => [...xs, { ...d, key: Date.now() + xs.length, amount: '' }]);

  const amt = amounts(amount, included);
  const distSum = dists.reduce((s, d) => s + Number(digits(d.amount) || 0), 0);

  async function submit() {
    setError(null);
    if (!kind) return setError('종류를 고르세요.');
    if (!corpId) return setError('법인을 고르세요.');
    if (!dasu && !cust.name.trim()) return setError('거래처를 입력하세요.');
    if (kind === '현금영수증' && !cust.cashId.trim()) return setError('현금영수증 발급 번호(휴대폰 또는 사업자번호)를 입력하세요.');
    if (!dasu && !amt) return setError('금액을 입력하세요.');
    if (dasu && dists.length === 0) return setError('배분 항목을 1개 이상 넣으세요.');
    if (dists.some((d) => !Number(digits(d.amount)))) return setError('배분 금액을 입력하세요.');
    if (!dasu && amt && distSum > amt.supply) return setError('배분 합계가 금액보다 큽니다.');
    setBusy('신청하는 중…');
    try {
      const r = await driverApi.requestOrder({
        kind, corpId, requestDate, memo: memo.trim() || undefined, attach,
        custName: cust.name.trim() || undefined, bizNumber: cust.biz.trim() || undefined, email: cust.email.trim() || undefined,
        phone: cust.phone.trim() || undefined, cashId: kind === '현금영수증' ? cust.cashId.trim() : undefined,
        supply: amt ? String(amt.supply) : undefined, vat: amt ? String(amt.vat) : undefined,
        dists: dists.map((d) => (d.driverId ? { driverId: d.driverId, amount: digits(d.amount) } : { type: d.type, name: d.label, amount: digits(d.amount) })),
        forDriverId: proxy?.driverId,
      });
      setDone(r.orderNo);
    } catch (e) { setError(driverError(e, '신청하지 못했습니다.')); } finally { setBusy(null); }
  }

  const title = kind ? `${kind} 신청` : '신청하기';
  if (done) {
    return (
      <Screen title={title} back={kind === '다수배분' ? '/dasu' : '/orders'}>
        <div className="driver-card p-6 text-center">
          <p className="text-2xl font-bold text-ink">{proxy ? `${proxyName(proxy)} 기사님 대신 신청했습니다` : '신청했습니다'}</p>
          <p className="mt-3 text-lg text-ink-soft">번호 <b className="text-ink">{done}</b></p>
          <p className="mt-2 text-base text-ink-soft">회계팀이 처리하면 목록에 「처리완료」로 바뀝니다.</p>
        </div>
        <button className="driver-btn mt-6" onClick={() => navigate(kind === '다수배분' ? '/dasu' : '/orders')}>목록으로</button>
      </Screen>
    );
  }
  if (!form) return <Screen title={title} back={kind === '다수배분' ? '/dasu' : '/orders'}>{error ? <ErrorBanner>{error}</ErrorBanner> : <Loading />}</Screen>;
  if (!form.open) {
    return (
      <Screen title={title} back={kind === '다수배분' ? '/dasu' : '/orders'}>
        <p className="rounded-xl bg-canvas px-4 py-8 text-center text-lg text-ink-soft">
          신청은 아직 <b className="text-ink">기사접수 앱</b>에서 받습니다.<br />여기서 받는 날짜는 회사에서 안내합니다.
        </p>
      </Screen>
    );
  }
  if (!kind) {
    return (
      <Screen title="무엇을 신청하나요?" back={kind === '다수배분' ? '/dasu' : '/orders'} acting>
        <div className="space-y-4">
          {KINDS.map((k) => (
            <button key={k.kind} className="flex min-h-touch w-full items-center gap-4 rounded-xl border-2 border-line bg-surface px-5 py-4 text-left" onClick={() => setKind(k.kind)}>
              <span className="text-2xl" aria-hidden>{k.icon}</span>
              <span><span className="block text-lg font-semibold text-ink">{k.kind}</span><span className="text-base text-ink-soft">{k.hint}</span></span>
            </button>
          ))}
        </div>
      </Screen>
    );
  }

  return (
    <Screen title={title} back={kind === '다수배분' ? '/dasu' : '/orders'} acting>
      <ProxyPicker value={proxy} onChange={changeProxy} disabled={busy !== null} />
      <div className="space-y-6">
        <Field label="신청자"><p className="text-lg font-semibold text-ink">{form.requester}</p></Field>
        <button className="text-base text-ink-soft underline" onClick={() => { setKind(null); setDists([]); }}>종류 다시 고르기</button>

        {form.corps.length > 1 && (
          <Field label="법인">
            <div className="grid grid-cols-2 gap-3">
              {form.corps.map((c) => (
                <button key={c.corp_id} className={`min-h-touch rounded-xl border-2 text-lg ${corpId === c.corp_id ? 'border-brand-600 bg-brand-50 font-bold text-brand-700' : 'border-line'}`}
                        onClick={() => setCorpId(c.corp_id)}>{c.short_name}</button>
              ))}
            </div>
          </Field>
        )}

        <Field label={dasu ? '거래처(선택)' : '거래처'}>
          <input className="driver-input" placeholder="거래처 이름(목록에 없으면 그대로 쓰세요)" value={q}
                 onChange={(e) => { setQ(e.target.value); setPicked(false); setCust((c) => ({ ...c, name: e.target.value })); }} />
          {found.length > 0 && (
            <ul className="driver-card mt-2 divide-y">
              {found.map((c, i) => (
                <li key={i}><button className="min-h-touch w-full px-4 py-2 text-left text-lg" onClick={() => pick(c)}>
                  {c.cust_name}<span className="ml-2 text-base text-ink-soft">{c.biz_number}</span></button></li>
              ))}
            </ul>
          )}
        </Field>
        {kind === '현금영수증' && (
          <Field label="현금영수증 발급 번호(휴대폰 또는 사업자번호)">
            <input className="driver-input" inputMode="numeric" value={cust.cashId} onChange={(e) => setCust({ ...cust, cashId: e.target.value })} />
          </Field>
        )}
        {!dasu && <Field label="사업자번호(선택)"><input className="driver-input" inputMode="numeric" value={cust.biz} onChange={(e) => setCust({ ...cust, biz: e.target.value })} /></Field>}
        {kind === '페이앱' && <Field label="발행 이메일(선택)"><input className="driver-input" type="email" value={cust.email} onChange={(e) => setCust({ ...cust, email: e.target.value })} /></Field>}
        {(kind === '페이앱' || kind === '현금영수증') && (
          <Field label="거래처 휴대폰(선택)"><input className="driver-input" inputMode="tel" value={cust.phone} onChange={(e) => setCust({ ...cust, phone: e.target.value })} /></Field>
        )}
        <Field label={dasu ? '실행일자' : '요청일자'}><input className="driver-input" type="date" value={requestDate} onChange={(e) => setRequestDate(e.target.value)} /></Field>

        {!dasu && (
          <Field label="금액">
            <div className="mb-3 grid grid-cols-2 gap-3">
              {[false, true].map((v) => (
                <button key={String(v)} className={`min-h-touch rounded-xl border-2 text-lg ${included === v ? 'border-brand-600 bg-brand-50 font-bold text-brand-700' : 'border-line'}`}
                        onClick={() => setIncluded(v)}>{v ? '부가세 포함' : '공급가(부가세 별도)'}</button>
              ))}
            </div>
            <input className="driver-input" inputMode="numeric" placeholder="원" value={amount ? won(digits(amount)) : ''} onChange={(e) => setAmount(digits(e.target.value))} />
            {amt && <p className="mt-2 text-base text-ink-soft">공급가 {won(amt.supply)} + 부가세 {won(amt.vat)} = 합계 <b className="text-ink">{won(amt.supply + amt.vat)}</b>원</p>}
          </Field>
        )}

        {ATTACH_LABEL[kind] && (
          <Field label={ATTACH_LABEL[kind]!}>
            {attach ? (
              <p className="flex items-center justify-between gap-3 text-lg"><span className="truncate">📎 {attach.name}</span>
                <button className="text-base text-ink-soft underline" onClick={() => setAttach(null)}>빼기</button></p>
            ) : (
              <div className="relative">
                <div className="flex min-h-touch items-center justify-center rounded-xl border-2 border-dashed border-line text-lg text-ink-soft">사진·PDF 올리기</div>
                <input type="file" accept="image/*,application/pdf" className="absolute inset-0 cursor-pointer opacity-0" aria-label="첨부 올리기" onChange={onFile} disabled={busy !== null} />
              </div>
            )}
          </Field>
        )}

        <Field label={dasu ? '배분 항목(1개 이상)' : '내부배분 — 다른 기사에게(선택)'}>
          {dists.map((d, i) => (
            <div key={d.key} className="mb-3 flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate text-lg">{d.label}{d.type && <span className="ml-1 text-sm text-ink-soft">({d.type === '외부배분-법인' ? '업체' : '개인'})</span>}</span>
              <input className="driver-input w-36" inputMode="numeric" placeholder="원" value={d.amount ? won(digits(d.amount)) : ''}
                     onChange={(e) => setDists(dists.map((x, j) => (j === i ? { ...x, amount: digits(e.target.value) } : x)))} />
              <button className="min-h-touch px-2 text-2xl text-ink-soft" aria-label="배분 빼기" onClick={() => setDists(dists.filter((_, j) => j !== i))}>×</button>
            </div>
          ))}
          <input className="driver-input" placeholder="기사 이름 또는 차량번호(예: 3899)" value={peerQ} onChange={(e) => setPeerQ(e.target.value)} />
          {peers.length > 0 && (
            <ul className="driver-card mt-2 divide-y">
              {peers.filter((p) => p.driverId !== proxy?.driverId && !dists.some((d) => d.driverId === p.driverId)).map((p) => (
                <li key={p.driverId}><button className="min-h-touch w-full px-4 py-2 text-left text-lg"
                  onClick={() => { addDist({ driverId: p.driverId, label: p.label }); setPeerQ(''); setPeers([]); }}>{p.label}</button></li>
              ))}
            </ul>
          )}
          {dasu && (
            <div className="mt-3 space-y-2">
              <input className="driver-input" placeholder="기사가 아닌 업체·개인 이름" value={extName} onChange={(e) => setExtName(e.target.value)} />
              <div className="grid grid-cols-2 gap-3">
                {(['외부배분-개인', '외부배분-법인'] as const).map((t) => (
                  <button key={t} className="min-h-touch rounded-xl border-2 border-line text-lg" disabled={!extName.trim()}
                          onClick={() => { addDist({ type: t, label: extName.trim() }); setExtName(''); }}>＋ {t === '외부배분-법인' ? '업체로 추가' : '개인으로 추가'}</button>
                ))}
              </div>
            </div>
          )}
          {!dasu && dists.length > 0 && amt && <p className="mt-2 text-base text-ink-soft">내 몫 {won(amt.supply - distSum)}원(공급가 기준)</p>}
          {dasu && dists.length > 0 && <p className="mt-2 text-base text-ink-soft">배분 합계 <b className="text-ink">{won(distSum)}</b>원</p>}
        </Field>

        <Field label="기타 입력사항(선택)"><textarea className="driver-input min-h-[6rem] py-3" value={memo} onChange={(e) => setMemo(e.target.value)} /></Field>
        {kind === '페이앱' && <p className="text-base text-ink-soft">🔒 카드번호는 여기에 적지 말고 카드 사진으로 올려 주세요.</p>}

        <SubmitError message={error} />
        <button className="driver-btn" disabled={busy !== null} onClick={() => void submit()}>{busy ?? '신청하기'}</button>
      </div>
    </Screen>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-2 text-base font-semibold text-ink-soft">{label}</p>
      {children}
    </div>
  );
}
